import { router } from "@inertiajs/react";
import { Autocomplete, Button, Menu, Select } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconDotsVertical, IconFilePlus } from "@tabler/icons-react";
import type { DataTableSortStatus } from "mantine-datatable";
import { useState } from "react";
import { FilterSection } from "../../components/filter/FilterSection";
import { ReportConfigsTable } from "../../components/reportConfig/ReportConfigsTable";
import { showDeleteModal } from "../../helpers/modal";
import { useYearContext } from "../../hooks/useYearContext";
import { ContentShell } from "../../layout/ContentShell";
import { getOccupationOptions, type Occupation } from "../../models/enums/occupation";
import {
  duplicateReportTypeConfigRoute,
  editReportTypeConfigRoute,
  getReportTypeOptions,
  newReportTypeConfigRoute,
  ReportType,
  reportTypeTranslations,
} from "../../models/enums/reportType";
import { type Group, getGroupOptions } from "../../models/group";
import type { Pagination } from "../../models/pagination";
import type { ReportConfiguration } from "../../models/reportConfigs/reportConfiguration";
import { getYearContextOptions, type YearContext } from "../../models/yearContext";
import { report_configuration_path, report_configurations_path } from "../../routes";

type ReportConfigsProps = {
  report_configs: ReportConfiguration[];
  config_titles: string[];
  year_contexts: YearContext[];
  groups: Group[];
  filter: FilterFormType & {
    sort_key?: "name";
    sort_direction?: "asc" | "desc";
  };
  pagination: Pagination;
};

type FilterFormType = {
  title?: string;
  report_type?: ReportType;
  year_context_id?: string;
  occupation?: Occupation;
  group_id?: string;
};

const resetFilters: FilterFormType = {
  title: undefined,
  report_type: undefined,
  year_context_id: undefined,
  occupation: undefined,
  group_id: undefined,
};

export default function ReportConfigs({ report_configs, filter, year_contexts, groups, config_titles, pagination }: ReportConfigsProps) {
  const { pathWithYearContext } = useYearContext();
  const [isFetching, setIsFetching] = useState(false);

  const { sort_key, sort_direction, ...filterFormData } = filter;

  const sortStatus: DataTableSortStatus<ReportConfiguration> = {
    columnAccessor: sort_key || "",
    direction: sort_direction || "asc",
  };

  const filterForm = useForm({
    mode: "uncontrolled",
    initialValues: {
      title: filterFormData?.title,
      report_type: filterFormData?.report_type,
      year_context_id: filterFormData?.year_context_id,
      occupation: filterFormData?.occupation,
      group_id: filterFormData?.group_id,
    },
  });

  const onDelete = (reportConfig: ReportConfiguration) =>
    showDeleteModal({
      article: "die",
      title: `Berichtsvorlage ${reportConfig.title}`,
      onConfirm: () => router.delete(pathWithYearContext(report_configuration_path, reportConfig.id)),
    });

  const handlePageUpdate = (filter: FilterFormType, sortStatus: DataTableSortStatus<ReportConfiguration>, page: number) => {
    setIsFetching(true);
    router.visit(pathWithYearContext(report_configurations_path), {
      only: ["report_configs", "filter", "pagination"],
      data: {
        filter: {
          ...filter,
          sort_key: sortStatus.columnAccessor,
          sort_direction: sortStatus.direction,
        },
        page,
      },
      onFinish: () => setIsFetching(false),
    });
  };
  const onFilterSubmit = () => handlePageUpdate(filterForm.getValues(), sortStatus, pagination.page);
  const onSortChange = (sortStatus: DataTableSortStatus<ReportConfiguration>) =>
    handlePageUpdate(filterFormData, sortStatus, pagination.page);
  const onReset = () => handlePageUpdate(resetFilters, sortStatus, pagination.page);
  const onPageChange = (page: number) => handlePageUpdate(filterFormData, sortStatus, page);

  const onNew = (reportType: ReportType) => router.get(pathWithYearContext(newReportTypeConfigRoute[reportType]));
  const onEdit = (reportConfig: ReportConfiguration) =>
    router.get(pathWithYearContext(editReportTypeConfigRoute[reportConfig.report_type], reportConfig.id));
  const onDuplicate = (reportConfig: ReportConfiguration) =>
    router.get(pathWithYearContext(duplicateReportTypeConfigRoute[reportConfig.report_type]), { id: reportConfig.id });

  const menuItems = Object.values(ReportType).map((type) => (
    <Menu.Item key={type} leftSection={<IconFilePlus size={20} />} onClick={() => onNew(type)}>
      {reportTypeTranslations[type]}
    </Menu.Item>
  ));

  const menu = (
    <Menu>
      <Menu.Target>
        <Button rightSection={<IconDotsVertical size={20} />} variant="light">
          Neu
        </Button>
      </Menu.Target>
      <Menu.Dropdown>{...menuItems}</Menu.Dropdown>
    </Menu>
  );

  return (
    <ContentShell title="Berichtvorlagen" actions={menu}>
      <FilterSection
        form={filterForm}
        onSubmit={onFilterSubmit}
        onReset={onReset}
        filters={[
          <Autocomplete data={config_titles} label="Titel" key={filterForm.key("title")} {...filterForm.getInputProps("title")} />,
          <Select
            label="Typ"
            clearable
            data={getReportTypeOptions()}
            key={filterForm.key("report_type")}
            {...filterForm.getInputProps("report_type")}
          />,
          <Select
            label="Jahrgang"
            clearable
            data={getYearContextOptions(year_contexts)}
            key={filterForm.key("year_context_id")}
            {...filterForm.getInputProps("year_context_id")}
          />,
          <Select
            label="Beruf"
            clearable
            data={getOccupationOptions()}
            key={filterForm.key("occupation")}
            {...filterForm.getInputProps("occupation")}
          />,
          <Select
            label="Gruppe"
            clearable
            data={getGroupOptions(groups)}
            key={filterForm.key("group_id")}
            {...filterForm.getInputProps("group_id")}
          />,
        ]}
      />
      <ReportConfigsTable
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        reportConfigs={report_configs}
        onSortStatusChange={onSortChange}
        sortStatus={sortStatus}
        pagination={pagination}
        onPageChange={onPageChange}
        fetching={isFetching}
      />
    </ContentShell>
  );
}
